import React, { useEffect, useState } from "react";
import { InputField, SelectField } from "../Petowner/AddOwnerModal";
import apiClient from "../../../services/apiClient";
import { SelectChangeEvent } from "@mui/material";
import { color } from "framer-motion";

export default function AddPetsModal({
    onClose,
    onSuccess,
    editData,
    mode = "add",
}: {
    onClose: () => void;
    onSuccess?: () => void;
    editData?: any;
    mode?: "add" | "edit";
}) {
    const fieldKeys = [
        "name", "species", "breed", "gender", "dob", "age", "weight", "size",
        "color", "sheds", "barking", "activity", "exercise", "grooming",
        "lifeSpan", "breedGroup", "coatLength", "energyLevel", "friendliness",
        "sizeOfHome", "sizeOfGarden", "weightLow", "weightHigh", "affectionNeeds",
        "characteristics", "easeOfTraining", "heightLowInches", "heightHighInches",
        "chipNumber", "kciFirstName", "kciLastName", "kciRegistrationNumber",
    ];

    const createInitialState = (keys: string[], defaultValue = "") =>
        Object.fromEntries(keys.map(k => [k, defaultValue]));

    const initialFormState = createInitialState(fieldKeys);

    const [formData, setFormData] = useState<Record<string, string>>(initialFormState);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const optionKeys = ["petType", "breed", "parent"];
    const [options, setOptions] = useState<Record<string, { label: string; value: string }[]>>(
        Object.fromEntries(optionKeys.map(k => [`${k}Options`, []]))
    );

    const [loadingState, setLoadingState] = useState<Record<string, boolean>>(
        Object.fromEntries(["main", "parents"].map(k => [`loading${k.charAt(0).toUpperCase() + k.slice(1)}`, false]))
    );


    useEffect(() => {
        const fetchMasters = async (slug: string, optionKey: string) => {
            setLoadingState(prev => ({ ...prev, main: true }));
            try {
                const res: any = await apiClient(`/v1/resource/masters/all?slug=${slug}`);
                const values = res?.data?.masters?.[0]?.attributes?.values ?? [];

                const mapped = values.map((v: string) => ({
                    label: v.charAt(0).toUpperCase() + v.slice(1),
                    value: v.toLowerCase(),
                }));

                setOptions(prev => ({ ...prev, [optionKey]: mapped }));
                console.log(`✅ ${slug} loaded:`, mapped);
            } catch (err) {
                console.error(`❌ Failed to load ${slug}:`, err);
            } finally {
                setLoadingState(prev => ({ ...prev, main: false }));
            }
        };

        fetchMasters("pet-type", "petTypeOptions");
    }, []);

    useEffect(() => {
        const fetchParents = async () => {
            setLoadingState(prev => ({ ...prev, loadingParents: true }));
            try {
                const res: any = await apiClient("/v1/resource/patients/all");
                const formatted =
                    res?.data?.patients?.map((p: any) => ({
                        label: `${p.user.first_name} ${p.user.last_name}`,
                        value: p.id,
                    })) ?? [];

                setOptions(prev => ({ ...prev, parentOptions: formatted }));
                console.log("✅ Parents loaded:", formatted.length);
            } catch (err) {
                console.error("❌ Failed to fetch parents:", err);
            } finally {
                setLoadingState(prev => ({ ...prev, loadingParents: false }));
            }
        };

        fetchParents();
    }, []);

    useEffect(() => {
        if (!formData.species) return;

        const fetchBreeds = async () => {
            setLoadingState(prev => ({ ...prev, main: true }));

            try {
                const res: any = await apiClient(
                    `/v1/resource/masters/all?slug=${formData.species}&searchkey=&order_by=name&dir=1`
                );

                const mapped =
                    res?.data?.masters?.map((b: any) => ({
                        label: b.name,
                        value: b.slug,
                        full: b,
                    })) ?? [];

                setOptions(prev => ({ ...prev, breedOptions: mapped }));
                console.log(`✅ Breeds loaded for ${formData.species}:`, mapped.length);
            } catch (err) {
                console.error(`❌ Failed to load breeds for ${formData.species}:`, err);
                setOptions(prev => ({ ...prev, breedOptions: [] }));
            } finally {
                setLoadingState(prev => ({ ...prev, main: false }));
            }
        };

        fetchBreeds();
    }, [formData.species]);

    useEffect(() => {
        if (!formData.breed) return;

        const selectedBreed: any = options.breedOptions.find(b => b.value === formData.breed);
        const attrs = selectedBreed?.full?.attributes || {};

        const normalizeKey = (key: string) =>
            key.replace(/_/g, "").replace(/lbs|inches/g, "").toLowerCase();

        const fieldMap = [
            "size", "color", "sheds", "barking", "activity", "exercise", "grooming",
            "life_span", "breed_group", "coat_length", "energy_level", "friendliness",
            "size_of_home", "size_of_garden", "weight_low_lbs", "weight_high_lbs",
            "affection_needs", "characteristics", "ease_of_training",
            "height_low_inches", "height_high_inches",
        ];

        const breedData = Object.fromEntries(
            fieldMap.map(key => {
                const altKey = key.replace(/_/g, "");
                const value = attrs[key] ?? attrs[altKey] ?? "";
                return [normalizeKey(key), typeof value === "string" ? value.split(",")[0]?.trim() : value];
            })
        );

        setFormData(prev => ({ ...prev, ...breedData }));
    }, [formData.breed, options.breedOptions]);


    useEffect(() => {
        if (!formData.dob) return;

        const birth = new Date(formData.dob);
        const today = new Date();

        const diffDays = Math.floor((today.getTime() - birth.getTime()) / 86400000); // 1000*60*60*24
        const years = today.getFullYear() - birth.getFullYear() - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
        const months = (today.getMonth() - birth.getMonth() + 12 * (today.getFullYear() - birth.getFullYear())) % 12;

        const ageText =
            years > 0
                ? `${years} year${years > 1 ? "s" : ""}${months ? ` ${months} month${months > 1 ? "s" : ""}` : ""}`
                : months > 0
                    ? `${months} month${months > 1 ? "s" : ""}`
                    : `${diffDays} day${diffDays > 1 ? "s" : ""}`;

        setFormData(prev => ({ ...prev, age: ageText }));
    }, [formData.dob]);

    useEffect(() => {
        if (mode !== "edit" || !editData) return;
        const additional = editData.additional_info || {};
        const fieldMap: Record<string, string> = {
            primary_id: "primary_id",
            name: "name",
            species: "species",
            breed: "type",
            gender: "gender",
            dob: "dob",
            age: "age",
            weight: "weight",
            size: "size",
            color: "color",
            sheds: "sheds",
            barking: "barking",
            activity: "activity",
            exercise: "exercise",
            grooming: "grooming",
            lifeSpan: "life_span",
            breedGroup: "breed_group",
            coatLength: "coat_length",
            energyLevel: "energy_level",
            friendliness: "friendliness",
            sizeOfHome: "size_of_home",
            sizeOfGarden: "size_of_garden",
            weightLow: "weight_low_lbs",
            weightHigh: "weight_high_lbs",
            affectionNeeds: "affection_needs",
            characteristics: "characteristics",
            easeOfTraining: "ease_of_training",
            heightLowInches: "height_low_inches",
            heightHighInches: "height_hight_inches",
            chipNumber: "chipNo",
            kciFirstName: "kciFirstName",
            kciLastName: "kciLastName",
            kciRegistrationNumber: "regNo",
        };

        const mappedData = Object.entries(fieldMap).reduce((acc, [formKey, sourceKey]) => {
            let value = additional[sourceKey] ?? editData[sourceKey] ?? "";
            if (formKey === "primary_id" && value) value = value.toString();
            if (formKey === "gender" && value)
                value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
            acc[formKey] = value;
            return acc;
        }, {} as Record<string, string>);

        setFormData(prev => ({ ...prev, ...mappedData }));
    }, [editData, mode]);


    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        if (mode !== "edit" || !editData || !options.breedOptions?.length) return;

        const breedName = editData.additional_info?.type?.trim();
        if (!breedName) return;

        const lowerName = breedName.toLowerCase();
        const matchedBreed = options.breedOptions.find(
            ({ label, value }) =>
                label.toLowerCase() === lowerName || value.toLowerCase() === lowerName
        );

        setFormData(prev => ({
            ...prev,
            breed: matchedBreed?.value ?? breedName,
        }));
    }, [mode, editData, options.breedOptions]);


    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | SelectChangeEvent<string | number>
    ) => {
        const { name, value } = e.target as { name: string; value: string };
        setFormData((p) => ({ ...p, [name]: value }));
        setErrors((p) => ({ ...p, [name]: "" }));
    };

    const resetForm = (close = false) => {
        setFormData(initialFormState);
        setErrors({});
        if (close) onClose();
    };

    const validate = () => {
        const requiredFields = [
            { key: "name", message: "Pet name is required." },
            { key: "species", message: "Species is required." },
            { key: "breed", message: "Breed is required." },
            { key: "gender", message: "Please select gender." },
            { key: "dob", message: "Date of birth is required." },
            { key: "weight", message: "Weight is required." },
        ];

        const newErrors: Record<string, string> = {};

        requiredFields.forEach(({ key, message }) => {
            const value = formData[key];
            if (!value || value.trim() === "") {
                newErrors[key] = message;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        try {
            const petTypeSlug = formData.species?.toLowerCase() || "";
            const selectedBreed = options.breedOptions.find(b => b.value === formData.breed);

            // ✅ Keys to include inside `additional_info`
            const additionalKeys = [
                "weight", "kciFirstName", "kciLastName", "name", "species", "color",
                "chipNumber", "kciRegistrationNumber", "gender", "age", "size", "sheds",
                "barking", "activity", "exercise", "grooming", "lifeSpan", "breedGroup",
                "coatLength", "energyLevel", "friendliness", "sizeOfHome", "sizeOfGarden",
                "weightLow", "weightHigh", "affectionNeeds", "characteristics",
                "easeOfTraining", "heightLowInches", "heightHighInches", "dob"
            ];

            const additional_info = Object.fromEntries(
                additionalKeys.map(key => [key, formData[key] || ""])
            );
            Object.assign(additional_info, {
                mrn: "",
                status: "",
                regNo: additional_info.kciRegistrationNumber,
                chipNo: additional_info.chipNumber,
                life_span: additional_info.lifeSpan,
                breed_group: additional_info.breedGroup,
                coat_length: additional_info.coatLength,
                energy_level: additional_info.energyLevel,
                size_of_home: additional_info.sizeOfHome,
                size_of_garden: additional_info.sizeOfGarden,
                weight_low_lbs: additional_info.weightLow,
                weight_high_lbs: additional_info.weightHigh,
                affection_needs: additional_info.affectionNeeds,
                ease_of_training: additional_info.easeOfTraining,
                height_low_inches: additional_info.heightLowInches,
                height_hight_inches: additional_info.heightHighInches,
                petType: petTypeSlug,
                type: selectedBreed?.label || "",
            });

            const payload = {
                primary_id: formData.primary_id,
                status: "active",
                type: petTypeSlug,
                additional_info,
            };

            console.log("🐶 Final Payload:", JSON.stringify(payload, null, 2));

            const isEdit = mode === "edit";
            const url = isEdit
                ? `/v1/resource/pets/${editData.id}`
                : `/v1/resource/pets`;

            const method = isEdit ? "PATCH" : "POST";

            await apiClient(url, { method, body: payload });

            alert(isEdit ? "Pet updated successfully!" : "Pet added successfully!");
            resetForm(true);
            onSuccess?.();
        } catch (error: any) {
            console.error("❌ API Error:", error);
            alert(error.message || "Something went wrong.");
        }
    };





    const fields = [
        {
            label: "Select Parent",
            name: "primary_id",
            type: "select",
            options: options?.parentOptions,
            required: true,
        },
        { label: "Pet Name", name: "name", required: true },
        { label: "Species (Pet Type)", name: "species", type: "select", options: options?.petTypeOptions, required: true },
        { label: "Breed", name: "breed", type: "select", options: options?.breedOptions, required: true },
        { label: "Gender", name: "gender", type: "select", required: true, options: ["Male", "Female", "Other"].map((v) => ({ label: v, value: v })) },
        { label: "Date of Birth", name: "dob", type: "date", required: true },
        { label: "Age", name: "age", readOnly: true },
        { label: "Weight (kg)", name: "weight", type: "number", unit: "kg", required: true },

        // Breed auto fields
        { label: "Size", name: "size", readOnly: true },
        { label: "Color", name: "color", readOnly: true },
        { label: "Sheds", name: "sheds", readOnly: true },
        { label: "Barking", name: "barking", readOnly: true },
        { label: "Activity", name: "activity", readOnly: true },
        { label: "Exercise", name: "exercise", readOnly: true },
        { label: "Grooming", name: "grooming", readOnly: true },
        { label: "Life Span", name: "lifeSpan", readOnly: true },
        { label: "Breed Group", name: "breedGroup", readOnly: true },
        { label: "Coat Length", name: "coatLength", readOnly: true },
        { label: "Energy Level", name: "energyLevel", readOnly: true },
        { label: "Friendliness", name: "friendliness", readOnly: true },
        { label: "Size of Home", name: "sizeOfHome", readOnly: true },
        { label: "Size of Garden", name: "sizeOfGarden", readOnly: true },
        { label: "Weight Low", name: "weightLow", readOnly: true },
        { label: "Weight High", name: "weightHigh", readOnly: true },
        { label: "Affection Needs", name: "affectionNeeds", readOnly: true },
        { label: "Characteristics", name: "characteristics", readOnly: true },
        { label: "Ease of Training", name: "easeOfTraining", readOnly: true },
        { label: "Height Low (inches)", name: "heightLowInches", readOnly: true },
        { label: "Height High (inches)", name: "heightHighInches", readOnly: true },

        // Manual entry fields
        { label: "Chip Number", name: "chipNumber" },
        { label: "KCI First Name", name: "kciFirstName" },
        { label: "KCI Last Name", name: "kciLastName" },
        { label: "KCI Registration Number", name: "kciRegistrationNumber" },
    ];


    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            {(loadingState.loadingMain || loadingState.loadingParents) && (
                <div className="absolute inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center z-50">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-700 font-medium">
                        {loadingState.loadingParents ? "Loading parents..." : "Loading data..."}
                    </p>
                </div>
            )}
            <div className="bg-white p-6 rounded-lg w-[1200px] max-w-[95vw] max-h-[90vh] overflow-y-auto shadow-lg">
                <h2 className="text-xl font-semibold mb-6 text-center border-b pb-2 text-gray-800">
                    {mode === "edit" ? "Edit Pet" : "Add Pet"}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {fields
                        // 🔹 Hide breed auto fields until breed is selected
                        .filter((f) => {
                            // hide all breed auto fields if breed is not chosen
                            const breedAutoFieldNames = [
                                "size", "color", "sheds", "barking", "activity", "exercise",
                                "grooming", "lifeSpan", "breedGroup", "coatLength", "energyLevel",
                                "friendliness", "sizeOfHome", "sizeOfGarden", "weightLow", "weightHigh",
                                "affectionNeeds", "characteristics", "easeOfTraining",
                                "heightLowInches", "heightHighInches",
                            ];

                            if (!formData.breed && breedAutoFieldNames.includes(f.name)) return false;

                            return true;
                        })
                        .map((f) =>
                            f.type === "select" ? (
                                <SelectField
                                    key={f.name}
                                    label={f.label}
                                    name={f.name}
                                    value={formData[f.name]}
                                    options={f.options}
                                    onChange={handleChange}
                                    error={errors[f.name]}
                                    disabled={loadingState.loadingMain && f.name === "species"}
                                    required={f.required}
                                />
                            ) : (
                                <InputField
                                    key={f.name}
                                    label={f.label}
                                    name={f.name}
                                    value={formData[f.name]}
                                    onChange={handleChange}
                                    error={errors[f.name]}
                                    type={f.type || "text"}
                                    unit={f.unit}
                                    required={f.required}
                                    disabled={f.name === "age" || f.readOnly}
                                />
                            )
                        )}
                </div>


                <div className="border-t pt-4 mt-6 flex justify-center gap-3">
                    <button
                        onClick={() => resetForm(true)}
                        className="px-5 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-5 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-all"
                    >
                        {mode === "edit" ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
